import React, { useContext, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import Layout from '../../components/layout/layout'
import Reveal from '../../components/Reveal/Reveal'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import Seo, { breadcrumbJsonLd, serviceDetailJsonLd, faqJsonLd } from '../../components/Seo/Seo'
import useEntryMotion from '../../components/motion/useEntryMotion'
import AbstractVisual from '../../components/AbstractVisual/AbstractVisual'
import { industryPhotos } from '../../data/photos'
import { resolveProjects } from '../../data/projects'
import ProjectMedia from '../../components/ProjectMedia/ProjectMedia'
import { getService } from '../../data/services'
import { localizeService } from '../../data/localizeService'
import { localizeIndustry } from '../../data/localizeIndustry'
import QuoteForm from '../../components/QuoteForm/QuoteForm'

import '../services/services.scss'
import './industries.scss'
import RelatedArticles from '../../components/RelatedArticles/RelatedArticles'
import { INDUSTRY_ARTICLES } from '../../data/topicArticles'

const pad = (n) => String(n).padStart(2, '0')

/**
 * One page per industry, rendered from data/industries.js: hero, who it is
 * for, what we build, sectors, why us, outcomes, shipped work, FAQ, related
 * services and contact. Shares the service pages' layout and styles.
 */
const IndustryDetail = ({ industry: source }) => {
  const { translations } = useContext(LanguageContext)
  const industry = localizeIndustry(source, translations)
  const navigate = useNavigate()
  const animateIn = useEntryMotion()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [source.slug])

  const path = `/industries/${industry.slug}`
  const projects = resolveProjects(translations).filter((project) => industry.projects.includes(project.title))
  const services = industry.services.map(getService).filter(Boolean).map((item) => localizeService(item, translations))
  const industriesLabel = translations.navIndustries || 'Industries'

  const openProject = (link) => {
    if (link.startsWith('http')) {
      window.open(link, '_blank', 'noopener')
      return
    }
    navigate(link)
  }

  const hero = (delay) => ({
    initial: animateIn ? { opacity: 0, y: 40 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  })

  return (
    <Layout>
      <Seo
        title={industry.seoTitle}
        description={industry.seoDescription}
        image={projects[0] ? projects[0].img : undefined}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Industries', path: '/industries' },
            { name: source.name, path },
          ]),
          serviceDetailJsonLd({ name: source.title, description: industry.seoDescription, path, serviceType: `Software development for ${source.name}` }),
          faqJsonLd(source.faq),
        ]}
      />

      <section className='svc-hero'>
        <div className='svc-inner'>
          <nav className='svc-breadcrumb' aria-label='Breadcrumb'>
            <Link to='/'>{translations.uiHome}</Link>
            <span aria-hidden='true'>/</span>
            <Link to='/industries'>{industriesLabel}</Link>
            <span aria-hidden='true'>/</span>
            <span aria-current='page'>{industry.name}</span>
          </nav>

          <div className='svc-hero-grid'>
            <div className='svc-hero-copy'>
              <motion.p className='svc-eyebrow' {...hero(0)}>{industry.name}</motion.p>
              <motion.h1 className='svc-hero-title svc-detail-title' {...hero(0.08)}>{industry.title}</motion.h1>
              <motion.p className='svc-hero-tagline' {...hero(0.16)}>{industry.tagline}</motion.p>
              <motion.p className='svc-hero-text' {...hero(0.24)}>{industry.lead}</motion.p>

              <motion.div className='svc-actions' {...hero(0.32)}>
                <Link to='/contact-us' className='svc-btn svc-btn-primary'>{translations.svcHeroPrimary}</Link>
                <Link to='/estimate' className='svc-btn svc-btn-ghost'>{translations.svcEstimateBtn}</Link>
              </motion.div>
            </div>
            <motion.div className='svc-hero-visual-wrap' {...hero(0.2)}>
              {industryPhotos[industry.slug] ? (
                <img className='svc-hero-visual svc-hero-photo' src={industryPhotos[industry.slug]} alt='' />
              ) : (
                <AbstractVisual className='svc-hero-visual' shape={industry.visual.shape} colors={industry.visual.colors} />
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <section className='svc-section svc-statement'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display'>{industry.statement}</h2>
          </Reveal>
          <div className='svc-statement-cols'>
            {industry.intro.map((paragraph, index) => (
              <Reveal key={index} delay={0.1 * (index + 1)}><p>{paragraph}</p></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className='svc-section'>
        <div className='svc-inner'>
          <Reveal>
            <p className='svc-eyebrow'>{industry.name}</p>
            <h2 className='svc-display'>{translations.indBuildTitle || 'What we build'}</h2>
          </Reveal>
          <div className='ind-build'>
            {industry.solutions.map((solution, index) => (
              <Reveal key={solution.title} delay={(index % 3) * 0.06} className='ind-build-wrap'>
                <article className='ind-build-card'>
                  <span className='ind-build-num' aria-hidden='true'>{pad(index + 1)}</span>
                  <h3 className='ind-build-title'>{solution.title}</h3>
                  <p className='ind-build-text'>{solution.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className='svc-section ind-sectors-section'>
        <div className='svc-inner'>
          <Reveal>
            <p className='svc-eyebrow'>{industry.name}</p>
            <h2 className='svc-display'>{translations.indWhoTitle || 'Who we work with'}</h2>
          </Reveal>
          <ul className='ind-sectors'>
            {industry.sectors.map((sector, index) => (
              <li key={sector.title}>
                <Reveal delay={(index % 3) * 0.06} className='ind-sector'>
                  <span className='ind-sector-num' aria-hidden='true'>{pad(index + 1)}</span>
                  <h3 className='ind-sector-title'>{sector.title}</h3>
                  <p className='ind-sector-text'>{sector.text}</p>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className='svc-section svc-deep'>
        <div className='svc-inner'>
          <Reveal>
            <p className='svc-eyebrow'>{translations.indWhyEyebrow || 'Why MLMind'}</p>
            <h2 className='svc-display'>{translations.indWhyTitle || 'Why teams choose us'}</h2>
          </Reveal>
          <div className='svc-deep-grid'>
            {industry.reasons.map((reason, index) => (
              <Reveal key={reason.title} delay={(index % 2) * 0.06} className='svc-deep-item'>
                <h3 className='svc-deep-title'>
                  <span className='svc-deep-num'>{pad(index + 1)}</span>
                  {reason.title}
                </h3>
                <p>{reason.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className='svc-section'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display'>{translations.indOutcomesTitle || 'What changes for you'}</h2>
          </Reveal>
          <ol className='svc-stages svc-stages-detail'>
            {industry.outcomes.map((outcome, index) => (
              <li key={outcome.title} className='svc-stage'>
                <Reveal delay={index * 0.08}>
                  <span className='svc-stage-num'>{pad(index + 1)}</span>
                  <span className='svc-stage-name'>{outcome.title}</span>
                  <span className='svc-stage-text'>{outcome.text}</span>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {projects.length > 0 && (
        <section className='svc-section svc-work'>
          <div className='svc-inner'>
            <Reveal>
              <p className='svc-eyebrow'>{translations.svcWorkEyebrow}</p>
              <h2 className='svc-display'>{translations.indWorkTitle || 'Our work in this industry'}</h2>
            </Reveal>
            <div className='svc-work-grid'>
              {projects.map((project, index) => (
                <Reveal key={project.title} delay={index * 0.1}>
                  <a
                    className='svc-work-card'
                    href={project.link}
                    onClick={(e) => {
                      e.preventDefault()
                      openProject(project.link)
                    }}
                  >
                    <div className='svc-work-img-frame'>
                      <ProjectMedia project={project} loading='lazy' width='640' height='420' />
                    </div>
                    <p className='svc-work-tag'>{project.tag}</p>
                    <h3 className='svc-work-title'>
                      {project.title} <span aria-hidden='true'>→</span>
                    </h3>
                    <p className='svc-work-text'>{project.text}</p>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className='svc-section'>
        <div className='svc-inner svc-faq'>
          <Reveal>
            <h2 className='svc-display'>{translations.svcFaqTitle}</h2>
          </Reveal>
          <div className='svc-faq-list'>
            {industry.faq.map((item, index) => (
              <details key={item.question} className='svc-faq-item' open={index === 0}>
                <summary>
                  <h3>{item.question}</h3>
                  <span className='svc-faq-icon' aria-hidden='true' />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className='svc-section svc-related-section'>
        <div className='svc-inner'>
          <Reveal>
            <h2 className='svc-display svc-display-sm'>{translations.indServicesTitle || 'Related services'}</h2>
          </Reveal>
          <div className='svc-related'>
            {services.map((item, index) => (
              <Reveal key={item.slug} delay={index * 0.08} className='svc-related-wrap'>
                <Link to={`/services/${item.slug}`} className='svc-link-card'>
                  <h3 className='svc-card-title'>{item.name}</h3>
                  <p className='svc-card-text'>{item.summary}</p>
                  <span className='svc-link-more'>{translations.uiLearnMore} <span aria-hidden='true'>→</span></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <RelatedArticles ids={INDUSTRY_ARTICLES[source.slug]} />

      <section className='svc-section svc-cta'>
        <div className='svc-inner'>
          {/* The form is on the page: a visitor who arrived from search can write
              without first finding the contact page. */}
          <div className='svc-cta-grid'>
            <Reveal>
              <p className='svc-eyebrow svc-eyebrow-light'>{translations.svcCtaEyebrow}</p>
              <h2 className='svc-cta-title'>{translations.svcCtaTitle}</h2>
              <p className='svc-cta-text'>{translations.svcCtaText}</p>
              <Link to='/contact-us' className='svc-btn svc-btn-light'>{translations.svcCtaButton}</Link>
            </Reveal>
            <Reveal delay={0.1}>
              <QuoteForm
                source={`Industry page: ${source.name}`}
                subject={`New project enquiry: ${source.name}`}
                topic={source.name}
                formName='industry'
              />
            </Reveal>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default IndustryDetail
